import type { Card } from "./card";
import type { DeckConfig } from "../engine/deck";
import type { HouseRules, StreakLock, StreakTarget } from "../engine/houseRules";

/** The 5 table piles are arranged in a "+" shape in the UI. */
export type StackIndex = 0 | 1 | 2 | 3 | 4;
export const STACK_INDICES: StackIndex[] = [0, 1, 2, 3, 4];
/** UI layout hint: which screen position each stack index maps to. */
export const STACK_LAYOUT: Record<StackIndex, "center" | "up" | "down" | "left" | "right"> = {
  0: "center",
  1: "up",
  2: "down",
  3: "left",
  4: "right",
};

export type GuessType = "higher" | "lower" | "match";

export type AIDifficulty = "easy" | "medium" | "hard" | "insane";

export interface TableStack {
  /** top-to-bottom order: index 0 is the topmost/most-recently-placed (visible) card. */
  cards: Card[];
}

export interface PlayerState {
  id: string;
  nickname: string;
  isAI: boolean;
  aiDifficulty?: AIDifficulty;
  /**
   * A CPU that chases Constellations stars (`decideMove`): when any move it can
   * make might light one, it makes the likeliest. A campaign opponent's trait,
   * set per seat by the level; absent everywhere else, which is ordinary play.
   */
  aiSeeksStars?: boolean;
  connected: boolean;
  /** top-to-bottom order: index 0 is the next card to be drawn. Never sent to other clients online. */
  deck: Card[];
  /**
   * Turns this player has finished this round.
   *
   * Counted so that "everyone gets the same number of turns" is a property the
   * engine can read rather than one it has to infer from seat order: a player
   * going out sets the round's turn count, and `endTurn` keeps dealing turns to
   * whoever is still short of it. Seat order alone would answer the same
   * question only for as long as every round starts on seat 0.
   */
  turnsTaken: number;
  /**
   * Turns finished in a row without taking a pile (Jourdan's Streaks).
   *
   * Counted at the end of every turn, and only while the rule is on — so a table
   * playing without it publishes zeroes rather than a number nothing can spend,
   * and no client has to re-check the rule before deciding whether to draw
   * flames. Spending the streak resets it; so does taking a pile.
   */
  streak: number;
  /**
   * Piles taken on consecutive turns (Drought, `engine/drought.ts`): at
   * `DROUGHT_MISSES_TO_MIST` the deck is wrapped in mist, and the next pile it
   * takes only half comes home. Counted only while the rule is in force, so a
   * table without it publishes zeroes and a client draws mist from this alone.
   * Optional because saves from before Drought lack it; missing reads as 0.
   */
  drought?: number;
  /**
   * The Mercy Rule's rest: own turns left before a burst can fire for this
   * player again (`MERCY_COOLDOWN_TURNS` after one, counted down at the end of
   * each of their turns). Optional; missing reads as 0, ready.
   */
  mercyRest?: number;
  /**
   * A Peek fired for this player: their own next card (`deck[0]`) is shown to
   * them — and, online, published as `peekCard` — until they play it.
   *
   * A flag rather than a count because two Peeks show the same card. It is
   * cleared at the START of their next `applyMove`, whatever the move, and by
   * nothing else: spending a streak is not playing a card, and a card taken off
   * their deck in between (a Burning Deck burn, a Meteor Swarm, a Comet Shower
   * passed to them) does not end it - the Peek just shows the new top card.
   * Optional because saves from before Peek existed lack it; missing reads as
   * false. Read it through `visiblePeekCard`, which also hides
   * it while its player is still mid-turn.
   */
  peeking?: boolean;
}

export type GamePhase =
  | "setup"
  | "playing"
  | "awaiting-bonus-choice"
  | "awaiting-swap-choice"
  | "round-over"
  | "game-over";

/**
 * Is somebody in the middle of a turn?
 *
 * "playing" is not the whole answer and never was: an off-by-one match parks the
 * round in `awaiting-bonus-choice` while the SAME player decides where their two
 * bonus cards go, and `currentPlayerIndex` still names them throughout — the
 * engine does not advance the turn until the placements are spent
 * (`applyBonusStackChoice`).
 *
 * This exists because four separate places had to know that, wrote it out by
 * hand, and one of them got it wrong: the table dropped the whose-turn-is-it
 * highlight off the acting player's deck for the whole bonus placement, so
 * everybody else was watching cards move with nothing on screen saying who was
 * moving them. A named question is harder to answer wrongly than a remembered
 * pair of string comparisons.
 *
 * The Swap Card's pause is the same shape: the turn is over in every sense but
 * the swaps its player still owes, so they stay on turn until they have made them.
 */
export function isTurnInProgress(phase: GamePhase): boolean {
  return phase === "playing" || phase === "awaiting-bonus-choice" || phase === "awaiting-swap-choice";
}

export interface PendingBonusChoice {
  type: "match-off-by-one-bonus";
  playerId: string;
  /** stack the player may not repeat-choose... actually they CAN choose any of the other 4; this is just the originating stack, excluded from eligibility. */
  originStackIndex: StackIndex;
  remainingPlacements: number; // starts at 2
}

/**
 * The Swap Cards a player fired this turn, owed at its end: each is one swap of
 * the top cards of two piles, chosen through `applySwapChoice`.
 */
export interface PendingSwapChoice {
  type: "swap-top-cards";
  playerId: string;
  remainingSwaps: number;
}

/**
 * Something the player on turn still has to decide before their turn can end.
 * Read `type` before anything else: the two shapes share only `playerId`.
 */
export type PendingDecision = PendingBonusChoice | PendingSwapChoice;

/**
 * One finished round's result, accumulated into the match record. Kept separate
 * from GameState so a match can outlive any individual round.
 */
export interface RoundSummary {
  roundNumber: number;
  /** playerId -> position points (1 = first out). */
  scores: Record<string, number>;
  /** playerId -> cards still in hand when the round ended, shown alongside points. */
  cardsLeft: Record<string, number>;
  winnerPlayerId: string | null;
  /**
   * Everyone who went out this round. One entry on an ordinary table; one per
   * table when a party folds its instances together, which is the case
   * `winnerPlayerId` alone cannot describe — it holds a single id, so every
   * winner but the first table's counted as having won nothing.
   *
   * Optional because rounds banked before this existed have no such list; read
   * it through `roundWinnerIds` rather than directly.
   */
  winnerPlayerIds?: string[];
  finishedAtMs: number;
  /**
   * Online only: what each member who WATCHED this round was given for it -
   * last place's score in it, so sitting a round out is never a way up the
   * board. Apart from `scores` because they did not play; in the totals all
   * the same. Absent when nobody sat out.
   */
  satOut?: Record<string, number>;
}

/** The round's winners, falling back to the single id on a record written before the list existed. */
export function roundWinnerIds(round: Pick<RoundSummary, "winnerPlayerId" | "winnerPlayerIds">): string[] {
  if (round.winnerPlayerIds?.length) return round.winnerPlayerIds;
  return round.winnerPlayerId ? [round.winnerPlayerId] : [];
}

/**
 * A match is just "however many rounds these players have played together so far".
 * It never formally ends — players simply stop starting new rounds, and the match
 * leaderboard is available at any point. See DESIGN_raw.md "Rounds and matches".
 */
export interface MatchState {
  matchId: string;
  /** 1-based number of the round currently being played (or just finished). */
  roundNumber: number;
  /** playerId -> sum of position points across every completed round. */
  totals: Record<string, number>;
  rounds: RoundSummary[];
  /**
   * playerId -> the name that id was last seen under, for every id on the board.
   *
   * The board's names otherwise come from the roster, and online a seat the host
   * removes leaves the roster while its points stay on the board — so it was
   * drawn under a raw uid. Written by `recordRound` here and, on the server, also
   * on the way out (`RemovePlayer`), when a joiner is seeded, and when a seat is
   * re-taken. Never pruned online: outliving the seat is the point. Locally a
   * departed seat leaves the board entirely, and `reseatMatch` takes its name
   * with it.
   *
   * Optional because a local save or a document written before it existed has
   * none; read it under the live roster, which names anyone still seated.
   */
  nicknames?: Record<string, string>;
}

export function newMatchState(matchId: string): MatchState {
  return { matchId, roundNumber: 1, totals: {}, rounds: [] };
}

export interface GameState {
  id: string;
  phase: GamePhase;
  /** The assembled deck for this round: size in half-deck increments plus wild toggles. */
  deckConfig: DeckConfig;
  /** Optional rules the host switched on for this table. */
  houseRules: HouseRules;
  players: PlayerState[];
  currentPlayerIndex: number;
  stacks: [TableStack, TableStack, TableStack, TableStack, TableStack];
  turnTimerSeconds: number | null;
  /**
   * Piles currently shut by spent streaks, each tagged with the player who shut
   * it — that is what releases it, at the start of their next turn.
   *
   * A flat list of one-pile entries rather than a per-player list of piles
   * because it also has to survive Firestore, which refuses an array directly
   * inside an array.
   */
  streakLocks: StreakLock[];
  /**
   * Players a spent streak has marked, and how many piles will shut when their
   * turn begins. Emptied one player at a time, as each of their turns comes up.
   */
  streakTargets: StreakTarget[];
  pendingDecision: PendingDecision | null;
  /**
   * Starfall's meter, bounty and event deck (`engine/starfall.ts`). Present only
   * on a round dealt with the rule in force; absent reads as the rule being off,
   * which is what every hand-built state (tests, tutorial, staged boards) and
   * every save from before Starfall gets.
   */
  starfall?: StarfallState;
  /**
   * The Mercy Rule's generator (`engine/mercy.ts`). Present only on a round
   * dealt with the rule in force; absent reads as the rule being off, for the
   * reason Starfall's is - hand-built states (tests, the tutorial, staged
   * boards) never burst a deck without asking. Server-only online.
   */
  mercy?: MercyState;
  /**
   * Drought's generator (`engine/drought.ts`), which picks where a mist
   * scatters. Present only on a round dealt with the rule in force; absent
   * reads as the rule being off, for Mercy's reason. Server-only online.
   */
  drought?: DroughtState;
  /**
   * Burning Deck's tally (`engine/burningDeck.ts`). Present only on a round dealt
   * with the rule in force; absent reads as the rule being off, for Mercy's
   * reason - hand-built states (tests, the tutorial, staged boards) never burn a
   * card without asking. Server-only online.
   */
  burningDeck?: BurningDeckState;
  /**
   * Swap Cards fired so far in the turn being played, not yet parked as a
   * `PendingSwapChoice`. Turn-scoped: `endTurn` turns it into the pending
   * decision (or forfeits it) and zeroes it, so it is 0 between turns. Optional
   * for saves from before Swap existed.
   */
  swapsOwed?: number;
  /**
   * Whether the turn parked on a swap choice had already taken a pile. The turn
   * ends in `applySwapChoice`, whose events cannot say so, and the streak is
   * scored off that answer. False (or absent) whenever no swap is pending.
   */
  swapTurnTookPile?: boolean;
  /** The FIRST player out this round; the round runs on past them (see `endTurn`). */
  winnerPlayerId: string | null;
  /**
   * Every player who went out this round, in the order they went out, for the
   * match record.
   *
   * More than one is ordinary rather than exceptional: the last lap gives every
   * player the same number of turns, so a second player can empty their deck
   * before the round ends. They finish level on cards and position scoring ties
   * them on points, and this is what says so — `winnerPlayerId` holds one id and
   * would count the rest as having won nothing. A party's parent fills it the
   * same way, from every table's list at once.
   */
  roundWinnerIds?: string[];
  /** set once phase becomes round-over or game-over: playerId -> position points (1 = first out). */
  scores: Record<string, number> | null;
  /** set alongside `scores`: playerId -> cards left in hand, for display next to the points. */
  cardsLeft: Record<string, number> | null;
  /** Which round of the ongoing match this is (1-based). */
  roundNumber: number;
  /**
   * The turn count this round has been fixed at by a player going out somewhere
   * else in the same party — online party tables only (docs/PARTY_MODE.md), 0 or
   * absent everywhere else.
   *
   * The equal-turns rule read across every table at once: a lobby split over
   * several tables is one game, so the first player out anywhere fixes the
   * round's length for all of them. `nextSeat` reads it exactly as it reads a
   * winner at this table, which is why the rule travels as a NUMBER — a table
   * can be in its last lap with nobody sitting at it out.
   *
   * Never written by a move. The party's server-side orchestration pushes it to
   * each table (`applyPartyLastLap`); an instance never reads its siblings.
   */
  partyLastLapTurns?: number;
  /** Monotonically increasing counter, bumped on every mutation. Useful for online sync/optimistic-concurrency. */
  version: number;
}

/** The table events a full Starfall meter can draw. Wire order; never re-sort - a new kind goes on the end. */
export type StarfallEventKind = "comets" | "storm" | "bounty" | "meteors" | "swarm";
export const STARFALL_EVENT_KINDS: StarfallEventKind[] = ["comets", "storm", "bounty", "meteors", "swarm"];

/** What players call each event - the settings checkboxes and the lobby's summary line. */
export const STARFALL_EVENT_NAMES: Record<StarfallEventKind, string> = {
  comets: "Comet Shower",
  storm: "Card Storm",
  bounty: "Bounty Pile",
  meteors: "Meteor Strike",
  swarm: "Meteor Swarm",
};

/**
 * One meteor of a Meteor Strike, in the order they land. A small one burns the
 * pile's top card off; the `big` one, last, destroys every value card on it as
 * a Smash would. Wilds are never destroyed: a small meteor on a wild top is
 * `resisted` and takes nothing, and the big one leaves any wilds standing.
 *
 * `burned` is the top card that went (null when the top survived or the pile
 * was empty), `destroyed` how many cards the pile lost in all, and `top` /
 * `count` the pile as the meteor left it - the buried cards are private online,
 * as they are for any pile.
 */
export interface MeteorStrike {
  stackIndex: StackIndex;
  big: boolean;
  burned: Card | null;
  destroyed: number;
  top: Card | null;
  count: number;
  resisted?: boolean;
}

/** How a Card Storm splits the table: see `stormSizes`. */
export type StormShape = "even" | "scattered" | "tower";

/** The piles a Bounty Pile event lit (ascending), and how many more turns they burn for. */
export interface StarfallBounty {
  stackIndices: StackIndex[];
  turnsLeft: number;
}

/**
 * Starfall's whole state. `stars` and `bounty` are public; the rest is the
 * server's alone online (the event deck's order and the generator decide what
 * happens next, exactly as deck order does).
 */
export interface StarfallState {
  /** Stars lit on the meter. Fires at `STARFALL_METER_SIZE`; a spare carries over. */
  stars: number;
  bounty: StarfallBounty | null;
  /**
   * The charge level last seen for each value, indexed by value (0 unused):
   * max(0, tops showing it - 2). A star is lit for every level that goes UP, so
   * a triplet charges when it appears and never again while it merely stands.
   */
  levels: number[];
  /**
   * The values of the staircase last seen on the tops, ascending; empty when
   * none stood. A star is lit for every step that is NEW (`staircaseGain`).
   * Optional for a round saved before staircases charged, which reads as none.
   */
  stair?: number[];
  /** Events still to draw this cycle, next first. Refilled and reshuffled when empty. */
  deck: StarfallEventKind[];
  /** The event drawn last, so a reshuffle never deals it again straight away. */
  last: StarfallEventKind | null;
  /** mulberry32 state, stepped in place (`nextStarfallRandom`). */
  rng: number;
}

/** The Mercy Rule's state: its generator, and the deal its line is measured against. */
export interface MercyState {
  /** mulberry32 state, stepped in place (`mercyPiles`). */
  rng: number;
  /**
   * Cards each seat was dealt this round (every seat gets the same), which
   * `mercyGap` halves into the lead over the field that bursts. A round saved
   * before it was recorded reads it as 0, and a 0 never bursts.
   */
  dealt: number;
}

/** Drought's state: the generator a mist scatters by. */
export interface DroughtState {
  /** mulberry32 state, stepped in place (`droughtPiles`). */
  rng: number;
}

/** Burning Deck's state: how many cards each player's fire has burned off their deck this round. */
export interface BurningDeckState {
  burned: Record<string, number>;
}

/** One card a Mercy burst threw off its player's deck, and the pile it landed on. */
export interface MercyCard {
  stackIndex: StackIndex;
  card: Card;
}

export type PlayerMove =
  | { type: "free-place"; stackIndex: StackIndex }
  | { type: "guess"; stackIndex: StackIndex; guess: GuessType };

export interface BonusStackChoice {
  stackIndex: StackIndex;
}

/** One owed swap: the two piles whose top cards trade places. */
export interface SwapChoice {
  stackIndices: [StackIndex, StackIndex];
}

/** Structured log of what happened while resolving a move; drives client-side animation. */
export type EngineEvent = (
  | {
      type: "card-played";
      playerId: string;
      stackIndex: StackIndex;
      card: Card;
      guess: GuessType | null;
      free: boolean;
      duplicate: boolean;
      /**
       * A guess card that was the last one in its player's deck when drawn,
       * whatever it then did. It describes the DRAW, not the outcome - a card
       * that loses the pile is flagged exactly like one that goes out - so the
       * suspense it earns on screen cannot give the result away. Absent on
       * free placements, and from the Go server when false.
       */
      lastCard?: boolean;
      /**
       * On a last card only: the turns its player had finished this round, and
       * the round. Seeds its suspense plan with when it was played, so the same
       * card replayed on the same pile does not repeat it. Absent otherwise.
       */
      turn?: number;
      round?: number;
    }
  | { type: "guess-result"; playerId: string; stackIndex: StackIndex; correct: boolean }
  | { type: "chain-bonus-card"; playerId: string; stackIndex: StackIndex; card: Card; continuesChain: boolean }
  | { type: "wild-played"; playerId: string; stackIndex: StackIndex; card: Card }
  /**
   * The card the wild's own player lays straight back on top of it, once the
   * wild has fired. It is never evaluated, so it simply stays — which is what
   * keeps the cleared pile with the player who cleared it instead of handing a
   * free placement to whoever is next. If that card is itself a wild it fires in
   * turn (its effect events follow this one) and gets a follow-up of its own.
   */
  | { type: "wild-follow-up-card"; playerId: string; stackIndex: StackIndex; card: Card }
  /**
   * A played Smash Card slammed its stack: `cardCount` VALUE cards beneath it are
   * removed from play entirely. Wilds beneath survive and stay in the pile, so a
   * slam can legitimately destroy nothing at all.
   */
  | { type: "wild-annihilation"; playerId: string; stackIndex: StackIndex; cardCount: number }
  /**
   * A played Lightning Card arced to the other stacks: the top card of each
   * listed stack was vaporized (removed from play). `stackIndex` is where the
   * Lightning itself landed; its own pile is left untouched beneath it.
   * `revealed` is the card left showing on that stack afterwards (null if the
   * strike emptied it) so the client can have it in place before the vaporized
   * card fades, with no blank frame in between.
   *
   * `resisted` marks a pile that was showing a WILD: the bolt reached it and the
   * card stayed, so `card` is still sitting on that pile and `revealed` is null.
   * The target is listed rather than skipped precisely so the client draws the
   * bolt and the deflection — a pile silently left out reads as a bug.
   */
  | {
      type: "lightning-strike";
      playerId: string;
      stackIndex: StackIndex;
      targets: { stackIndex: StackIndex; card: Card; revealed: Card | null; resisted?: boolean }[];
    }
  /**
   * A played Peek Card fired: `playerId` will see their own next card until they
   * play it. `stackIndex` is where the Peek landed. The card itself is not in
   * the event — events are broadcast to everyone, and it is only theirs to see.
   */
  | { type: "peek-activated"; playerId: string; stackIndex: StackIndex }
  /**
   * A played Swap Card fired. Nothing moves yet: the swap is owed until the end
   * of the turn, so the piles it chooses between are the ones the turn leaves.
   */
  | { type: "swap-owed"; playerId: string; stackIndex: StackIndex }
  /** The turn's owed swaps parked it in `awaiting-swap-choice`, waiting on its player. */
  | { type: "swap-choice-pending"; playerId: string; remainingSwaps: number }
  /**
   * One owed swap made: the top cards of the two piles traded places. `cards[i]`
   * is the card that WAS on `stackIndices[i]`, so it has just moved to the other.
   * Neither card is evaluated and neither fires, even a wild.
   */
  | {
      type: "stacks-swapped";
      playerId: string;
      stackIndices: [StackIndex, StackIndex];
      cards: [Card, Card];
      /**
       * `beneath[i]` is the card under `stackIndices[i]`'s top (null when that
       * pile held one card), so the animation can show it face up while the tops
       * fly. Carried here because the public pile view exposes only tops and
       * counts. Optional: events recorded before it existed lack it.
       */
      beneath?: (Card | null)[];
    }
  /** `bounty` when it landed on the Bounty Pile: every other pile then takes two cards, not one. */
  | { type: "five-out"; playerId: string; originStackIndex: StackIndex; bounty?: boolean }
  | { type: "five-out-bonus-card"; playerId: string; stackIndex: StackIndex; card: Card }
  /** `placements` is how many bonus cards are owed: 2, or 4 on the Bounty Pile. Absent means 2. */
  | { type: "match-off-by-one"; playerId: string; originStackIndex: StackIndex; placements?: number }
  | { type: "match-off-by-one-bonus-card"; playerId: string; stackIndex: StackIndex; card: Card }
  | { type: "match-off-by-two"; playerId: string; stackIndex: StackIndex }
  | { type: "match-fail"; playerId: string; stackIndex: StackIndex }
  | { type: "stack-taken"; playerId: string; stackIndex: StackIndex; cardCount: number; revealedCards?: Card[] }
  /**
   * The card the player who just took a pile immediately lays into the gap they
   * left. Never evaluated, so it always stays; a wild fires as usual (and takes
   * its own follow-up card). It stops the next player inheriting a free
   * placement they did nothing to earn.
   */
  | { type: "stack-refill-card"; playerId: string; stackIndex: StackIndex; card: Card }
  | { type: "free-placement-available" }
  /**
   * A player spent their streak: their deck's fire goes out and everyone in
   * `targets` will start their next turn with that many piles shut. The
   * spender's own turn has NOT been taken yet — this is a separate action in
   * front of it, so no `turn-ended` follows.
   *
   * A list rather than one id because Blue Shell marks every player tied for the
   * lead, and a single spend can therefore land on several of them at once. An
   * aimed streak is simply the one-entry case.
   */
  | { type: "streak-target"; playerId: string; streakTargets: StreakTarget[] }
  /**
   * The piles a marked player's turn actually opens with, shut as that turn
   * begins. Emitted from `endTurn` alongside the `turn-ended` that hands them
   * the table, and they all lift again when they are done.
   */
  | { type: "streak-locks"; playerId: string; stackIndices: StackIndex[] }
  | { type: "turn-ended"; playerId: string; nextPlayerId: string | null }
  /**
   * A player emptied their deck. It is the round's biggest moment, but it is no
   * longer the round's end: everyone still short of a turn gets one, and
   * `round-over` follows whenever that lap runs out. The two arrive in the same
   * batch when the player out was the last still owed a turn.
   */
  | { type: "player-won"; playerId: string }
  /**
   * Constellations: a triplet (or better) of `value` appeared on the tops of
   * `stackIndices`, lighting one star. `stars` is the meter after it.
   *
   * `pattern: "staircase"` is four or more tops in a row instead: `value` is
   * the lowest, and `stackIndices` holds one pile per step in ASCENDING VALUE
   * order - the order the client lights them in, so it must not be re-sorted.
   * Absent on a triplet.
   */
  | {
      type: "starfall-charge";
      playerId: string;
      value: number;
      stackIndices: StackIndex[];
      stars: number;
      pattern?: "staircase";
    }
  /** A full meter drew `event` off the event deck. It resolves in the events that follow. */
  | { type: "starfall-event"; playerId: string; event: StarfallEventKind; stars: number }
  /**
   * One Comet Shower card: it left `playerId`'s deck and landed on `stackIndex`,
   * unevaluated. A wild fires as usual, and its effect events follow this one.
   */
  | { type: "comet-card"; playerId: string; stackIndex: StackIndex; card: Card }
  /**
   * A Card Storm redealt every card on the table. `stacks` is the result, pile by
   * pile: how many cards and which one is on top (the buried cards are private
   * online, as they are for any pile).
   */
  | { type: "card-storm"; playerId: string; shape: StormShape; stacks: { count: number; top: Card | null }[] }
  /** A Meteor Strike: three meteors on three different piles, the last one big. Listed in landing order. */
  | { type: "meteor-strike"; playerId: string; strikes: MeteorStrike[] }
  /**
   * A Meteor Swarm: `playerId`'s top card left their deck and shattered into
   * `SWARM_SHARDS` burning shards, which hit in `strikes` order, each burning a
   * top card off as a small meteor does. `card` is the card that shattered;
   * absent (null in TS, omitted on the wire) when nobody held a card to give.
   */
  | { type: "meteor-swarm"; playerId: string; card?: Card | null; strikes: MeteorStrike[] }
  /**
   * The Mercy Rule: `playerId` ended their turn at least half a deal ahead of
   * the average opponent, and their top `mercyCards.length` cards (a third of
   * that gap, rounded up) burst off it, bound for the piles named, in landing
   * order. They land in the `mercy-card` events that follow, one each. Wire
   * name `mercyCards` because Go's event struct is flat.
   */
  | { type: "mercy-burst"; playerId: string; mercyCards: MercyCard[] }
  /**
   * One Mercy burst card landing on `stackIndex`, unevaluated. A wild fires and
   * takes its follow-up as usual, and a value card can light a star; their
   * events follow this one.
   */
  | { type: "mercy-card"; playerId: string; stackIndex: StackIndex; card: Card }
  /**
   * Drought: `playerId`'s misted deck missed on `stackIndex`, and the mist burst
   * the pile from the middle. `droughtCards` are the lower half's value cards,
   * in pile order (top first), each with the OTHER pile it landed on, in landing
   * order; they are on those piles already. The `stack-taken` that follows
   * carries only what came home. Wire name `droughtCards` because Go's event
   * struct is flat.
   */
  | { type: "drought-mist"; playerId: string; stackIndex: StackIndex; droughtCards: MercyCard[] }
  /**
   * Burning Deck: `playerId`'s deck had stayed alight, and `card` - what was on
   * top of it - burned away as their turn began. It is gone from the deck and
   * lands nowhere. The card is named (it is the player's own, and a burned one is
   * already off the table) so every client can draw it going.
   */
  | { type: "deck-burn"; playerId: string; card: Card }
  /** A Bounty Pile event lit `stackIndices` (usually two piles, ascending) for `turns` turns. */
  | { type: "bounty-set"; playerId: string; stackIndices: StackIndex[]; turns: number }
  /**
   * A bounty's free card, laid on the Bounty Pile after a correct Higher or
   * Lower (or a Match off by two). Unevaluated like any bonus card;
   * `continuesChain` when it extends a +/-1 run, whose chain cards follow.
   */
  | { type: "bounty-bonus-card"; playerId: string; stackIndex: StackIndex; card: Card; continuesChain: boolean }
  /** The Bounty Piles' lap is over; `stackIndices` are the piles going out. */
  | { type: "bounty-ended"; stackIndices: StackIndex[] }
  | { type: "round-over"; scores: Record<string, number>; cardsLeft: Record<string, number> })
  /**
   * Online only: the Go server stamps every event with its position in a
   * game-wide monotonic sequence so clients can dedupe and detect gaps across
   * out-of-order Firestore snapshots. Absent for local play, where events are
   * handed straight to the renderer in order.
   */
  & { seq?: number };

export interface ApplyMoveResult {
  state: GameState;
  events: EngineEvent[];
}
